import { resolveGraphicsConfig } from '@/graphics/GraphicsConfig'
import { createVideoRenderPresetDiagnostics, sanitizeVideoRenderDiagnostics } from './diagnostics'
import { normalizeGsplatDiagnostics } from './RenderStabilityGuard'
import type {
  BrowserVideoRenderRuntime,
  FrameReadbackResult,
  GsplatRenderableDiagnostics,
  ManualFrameRequest,
  VideoRenderConfig,
  VideoRenderDiagnosticStage,
  VideoRenderDiagnostics,
  VideoRenderEngineLike,
  VideoRenderRuntimeState,
} from './types'
import { createVideoRenderService, type VideoRenderService } from './VideoRenderService'

class BrowserVideoRenderRuntimeImpl implements BrowserVideoRenderRuntime {
  #state: VideoRenderRuntimeState = 'idle'
  #engine?: VideoRenderEngineLike
  #lastRenderedFrame?: ManualFrameRequest
  #disposed = false
  #service: VideoRenderService
  #defaultFrameReader?: ReturnType<VideoRenderService['createFrameReader']>
  readonly diagnostics: VideoRenderDiagnostics

  constructor(private readonly config: VideoRenderConfig) {
    this.#service = createVideoRenderService()
    this.diagnostics = {
      state: this.#state,
      requestedBackend: resolveGraphicsConfig(config.graphics).backend,
      canvas: {
        width: config.width,
        height: config.height,
        pixelRatio: config.pixelRatio ?? 1,
      },
      preset: createVideoRenderPresetDiagnostics(config.additionalPlugins),
    }
  }

  get state(): VideoRenderRuntimeState {
    return this.#state
  }

  async initialize(): Promise<void> {
    try {
      if (this.config.engineFactory) {
        this.#engine = await this.config.engineFactory(this.config)
        this.#engine.stop?.()
      }
      this.#refreshActualBackend()
      this.#setState('ready')
      this.#emit('create', 'runtime-ready')
    } catch (error) {
      this.#throwStageError(error, 'create', this.#classifyStageErrorCode(error, 'create'), true)
    }
  }

  async loadScene(_scene: unknown): Promise<void> {
    this.#assertActive('scene-load')
    this.#setState('loading')
    this.#emit('scene-load', 'scene-load-started')
    try {
      await (this.config.sceneLoader?.(_scene, this.#engine) ?? this.#engine?.loadScene?.(_scene))
      this.#setState('ready')
      this.#emit('scene-load', 'scene-load-complete')
    } catch (error) {
      this.#throwStageError(
        error,
        'scene-load',
        this.#classifyStageErrorCode(error, 'scene-load'),
        true
      )
    }
  }

  async waitForGsplatRenderable(signal?: AbortSignal): Promise<GsplatRenderableDiagnostics> {
    this.#assertActive('gsplat-stabilize')
    this.#emit('gsplat-stabilize', 'gsplat-stabilize-started')
    const guard =
      this.config.stabilityGuard ??
      this.#service.createStabilityGuard(
        this.#engine,
        this.config.stabilityOptions,
        this.config.frameReaderOptions
      )
    const gsplat = normalizeGsplatDiagnostics(await guard.waitForGsplatRenderable(signal))
    this.diagnostics.gsplat = gsplat
    if (gsplat.webgl) {
      this.diagnostics.webgl = gsplat.webgl
    }
    if (gsplat.stage === 'timeout' || gsplat.stage === 'error') {
      const error = {
        code:
          gsplat.stage === 'timeout'
            ? 'VIDEO_RENDER_GSPLAT_STABILIZE_TIMEOUT'
            : this.#classifyStageErrorCode(gsplat.failureReason, 'gsplat-stabilize'),
        stage: 'gsplat-stabilize' as const,
        message: gsplat.failureReason ?? 'GSplat renderable stabilization failed.',
        retryable: true,
      }
      this.diagnostics.lastError = error
      this.#emit('gsplat-stabilize', `gsplat-stabilize-${gsplat.stage}`)
      throw Object.assign(new Error(error.message), {
        code: error.code,
        diagnostics: sanitizeVideoRenderDiagnostics(this.diagnostics),
      })
    }
    this.#emit('gsplat-stabilize', 'gsplat-stabilize-ready')
    return gsplat
  }

  async applyClipTemplate(template: unknown): Promise<void> {
    await this.applyTimelineTemplate(template)
  }

  async applyTimelineTemplate(template: unknown): Promise<void> {
    this.#assertActive('template-apply')
    await this.#getTimelineAdapter()?.apply(template)
    this.#emit('template-apply', 'template-apply-placeholder')
  }

  async renderFrame(request: ManualFrameRequest): Promise<VideoRenderDiagnostics> {
    this.#assertActive('manual-frame')
    this.#validateManualFrameRequest(request)
    this.diagnostics.frame = request
    this.diagnostics.renderTarget = {
      width: request.width,
      height: request.height,
      pixelRatio: request.pixelRatio,
    }
    this.#setState('rendering')
    this.#emit('manual-frame', 'manual-frame-placeholder', request.frameNumber)
    try {
      await this.#getTimelineAdapter()?.seekAndApply(request.timestampSeconds)
      // backend capture 的 FrameReader 会在 capture 回调内重跑手动帧，避免默认 surface
      // 先渲染一次、采集时再渲染一次造成重复更新与空采集结果。
      if (!this.#usesCaptureBackedFrameReader()) {
        await this.#service.renderManualFrame(this.#engine, request)
      }
    } catch (error) {
      this.#throwRenderFrameError(error, request)
    }
    this.#lastRenderedFrame = request
    this.#setState('ready')
    return this.diagnostics
  }

  async readFrame(): Promise<FrameReadbackResult> {
    this.#assertActive('readback')
    const frame = this.#lastRenderedFrame
    if (!frame) {
      const error = {
        code: 'VIDEO_RENDER_READBACK_WITHOUT_FRAME',
        stage: 'readback' as const,
        message: 'readFrame requires a successful renderFrame call first.',
        retryable: false,
      }
      this.diagnostics.lastError = error
      throw Object.assign(new Error(error.message), {
        code: error.code,
        diagnostics: sanitizeVideoRenderDiagnostics(this.diagnostics),
      })
    }
    const reader = this.config.frameReader ?? this.#createDefaultFrameReader()
    const result = await reader.readFrame(frame)
    this.diagnostics.readback = result.diagnostics
    this.#emit('readback', 'readback-complete', frame.frameNumber)
    return result
  }

  async dispose(): Promise<void> {
    if (this.#disposed) {
      return
    }
    this.#disposed = true
    let cleanupError: unknown
    try {
      await this.#getTimelineAdapter()?.clear()
    } catch (error) {
      cleanupError = error
    } finally {
      try {
        await this.#engine?.dispose?.()
      } catch (error) {
        cleanupError ??= error
      }
      this.#setState('disposed')
      if (cleanupError) {
        this.diagnostics.lastError = {
          code: this.#classifyStageErrorCode(cleanupError, 'dispose'),
          stage: 'dispose',
          message:
            cleanupError instanceof Error
              ? cleanupError.message
              : 'Video render runtime cleanup failed.',
          retryable: false,
        }
      }
      this.#emit('dispose', 'runtime-disposed')
    }
  }

  #setState(state: VideoRenderRuntimeState): void {
    this.#state = state
    this.diagnostics.state = state
  }

  #assertActive(stage: VideoRenderDiagnosticStage): void {
    if (!this.#disposed) {
      return
    }
    const error = {
      code: 'VIDEO_RENDER_RUNTIME_DISPOSED',
      stage,
      message: 'Video render runtime has been disposed.',
      retryable: false,
    }
    this.diagnostics.lastError = error
    throw Object.assign(new Error(error.message), {
      code: error.code,
      diagnostics: sanitizeVideoRenderDiagnostics(this.diagnostics),
    })
  }

  #validateManualFrameRequest(request: ManualFrameRequest): void {
    const entries: Array<[keyof ManualFrameRequest, number | undefined]> = [
      ['frameNumber', request.frameNumber],
      ['timestampSeconds', request.timestampSeconds],
      ['deltaSeconds', request.deltaSeconds],
      ['width', request.width],
      ['height', request.height],
      ['pixelRatio', request.pixelRatio],
    ]
    const invalid = entries.find(([, value]) => value === undefined || !Number.isFinite(value))
    if (invalid) {
      this.#throwFrameError(`Invalid manual frame parameter: ${invalid[0]}`, request)
    }
    if (!Number.isInteger(request.frameNumber) || request.frameNumber < 0) {
      this.#throwFrameError('Invalid manual frame parameter: frameNumber', request)
    }
    if (request.timestampSeconds < 0 || request.deltaSeconds < 0) {
      this.#throwFrameError(
        'Invalid manual frame parameter: timestampSeconds/deltaSeconds',
        request
      )
    }
    if (request.width <= 0 || request.height <= 0 || request.pixelRatio <= 0) {
      this.#throwFrameError('Invalid manual frame parameter: width/height/pixelRatio', request)
    }
  }

  #throwFrameError(message: string, request: ManualFrameRequest): never {
    const error = {
      code: 'VIDEO_RENDER_INVALID_MANUAL_FRAME',
      stage: 'manual-frame' as const,
      message,
      retryable: false,
      frameNumber: request.frameNumber,
    }
    this.diagnostics.lastError = error
    throw Object.assign(new Error(message), {
      code: error.code,
      diagnostics: sanitizeVideoRenderDiagnostics(this.diagnostics),
    })
  }

  #throwRenderFrameError(cause: unknown, request: ManualFrameRequest): never {
    const message = cause instanceof Error ? cause.message : 'Manual frame render failed.'
    const error = {
      code: this.#classifyStageErrorCode(cause, 'manual-frame'),
      stage: 'manual-frame' as const,
      message,
      retryable: true,
      frameNumber: request.frameNumber,
    }
    this.#setState('error')
    this.diagnostics.lastError = error
    this.#emit('manual-frame', 'manual-frame-failed', request.frameNumber)
    throw Object.assign(new Error(message), {
      code: error.code,
      cause,
      diagnostics: sanitizeVideoRenderDiagnostics(this.diagnostics),
    })
  }

  #classifyStageErrorCode(cause: unknown, fallbackStage: VideoRenderDiagnosticStage): string {
    const message =
      typeof cause === 'string'
        ? cause
        : cause instanceof Error
          ? `${cause.name} ${cause.message}`
          : ''
    const normalized = message.toLowerCase()
    if (normalized.includes('context lost') || normalized.includes('webgl-context-lost')) {
      return 'VIDEO_RENDER_WEBGL_CONTEXT_LOST'
    }
    if (
      normalized.includes('page crash') ||
      normalized.includes('target closed') ||
      normalized.includes('browser has been closed')
    ) {
      return 'VIDEO_RENDER_PAGE_CRASHED'
    }
    if (
      normalized.includes('worker terminate') ||
      normalized.includes('worker terminated') ||
      normalized.includes('worker was terminated')
    ) {
      return 'VIDEO_RENDER_WORKER_TERMINATED'
    }
    if (fallbackStage === 'scene-load') {
      return 'VIDEO_RENDER_SCENE_LOAD_FAILED'
    }
    if (fallbackStage === 'manual-frame') {
      return 'VIDEO_RENDER_MANUAL_FRAME_FAILED'
    }
    if (fallbackStage === 'gsplat-stabilize') {
      return 'VIDEO_RENDER_GSPLAT_STABILIZE_FAILED'
    }
    if (fallbackStage === 'dispose') {
      return 'VIDEO_RENDER_RUNTIME_DISPOSE_FAILED'
    }
    return 'VIDEO_RENDER_RUNTIME_CREATE_FAILED'
  }

  #throwStageError(
    cause: unknown,
    stage: VideoRenderDiagnosticStage,
    code: string,
    retryable: boolean
  ): never {
    const message = cause instanceof Error ? cause.message : 'Video render stage failed.'
    const error = {
      code,
      stage,
      message,
      retryable,
    }
    this.#setState('error')
    this.diagnostics.lastError = error
    this.#emit(stage, `${stage}-failed`)
    throw Object.assign(new Error(message), {
      code: error.code,
      cause,
      diagnostics: sanitizeVideoRenderDiagnostics(this.diagnostics),
    })
  }

  #createDefaultFrameReader() {
    this.#defaultFrameReader ??= this.#service.createFrameReaderForEngine(
      this.#engine,
      this.config.frameReaderOptions
    )
    return this.#defaultFrameReader
  }

  #usesCaptureBackedFrameReader(): boolean {
    if (this.config.frameReader) {
      return false
    }
    return Boolean(
      this.#engine?.createRegionCaptureSession &&
        (this.#engine.renderManualFrame ?? this.#engine.loop?.renderManualFrame)
    )
  }

  #getTimelineAdapter() {
    return this.config.timelineAdapter ?? this.config.clipTemplateApplier
  }

  #refreshActualBackend(): void {
    const kind = this.#engine?.getGraphicsInfo?.().kind
    if (kind === 'webgl2' || kind === 'webgpu') {
      this.diagnostics.actualBackend = kind
    }
  }

  #emit(
    stage: Parameters<NonNullable<VideoRenderConfig['onDiagnosticEvent']>>[0]['stage'],
    name: string,
    frameNumber?: number
  ): void {
    this.#refreshActualBackend()
    const event = {
      stage,
      name,
      frameNumber,
      diagnostics: sanitizeVideoRenderDiagnostics(this.diagnostics),
    }
    this.config.onProgress?.(event)
    this.config.onDiagnosticEvent?.(event)
  }
}

export async function createBrowserVideoRenderRuntime(
  config: VideoRenderConfig
): Promise<BrowserVideoRenderRuntime> {
  const runtime = new BrowserVideoRenderRuntimeImpl(config)
  await runtime.initialize()
  return runtime
}
